import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard,
  SportsTennis,
  People,
  TrendingUp,
  Add,
  Menu as MenuIcon,
  Logout,
  Home,
  Event,
  Edit,
  Delete,
  Search,
  Email,
  Phone,
  Person,
  QrCode,
  Payment,
  CloudUpload,
  CheckCircle,
  ArrowBack,
  AttachMoney
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { playerAPI, tournamentAPI } from '../services/api';
import { v4 as uuidv4 } from 'uuid';

const PlayerManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Add CSS keyframes for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateY(0);
        }
        40% {
          transform: translateY(-4px);
        }
        60% {
          transform: translateY(-2px);
        }
      }
      @keyframes bounceIn {
        0% { transform: scale(0); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const { instance } = useMsal();
  
  // Local state for data
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API functions
  const loadPlayers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading players...');
      const response = await playerAPI.getAll();
      console.log('Players response:', response);
      console.log('Players data:', response.data);
      // The API returns { data: [...], pageIndex: 0, totalPages: 1, ... }
      // So we need to access response.data.data for the actual players array
      setPlayers(response.data?.data || []);
    } catch (error) {
      console.error('Error loading players:', error);
      console.error('Error details:', error.response?.data);
      setError(`Failed to load players: ${error.message}`);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTournaments = async () => {
    try {
      console.log('Loading tournaments...');
      const response = await tournamentAPI.getAll();
      console.log('Tournaments response:', response);
      console.log('Tournaments data:', response.data);
      // The API returns { data: [...], pageIndex: 0, totalPages: 1, ... }
      setTournaments(response.data?.data || []);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      setTournaments([]);
    }
  };

  const addPlayer = async (playerData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await playerAPI.create(playerData);
      console.log('Player created:', response);
      // Reload players after creating
      await loadPlayers();
      console.log('Player created successfully!');
    } catch (error) {
      console.error('Error creating player:', error);
      
      // Handle different types of API errors
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.invalidParams && data.invalidParams.length > 0) {
            // Display the first invalid parameter message
            const firstError = data.invalidParams[0];
            setError(firstError.message || 'Invalid input provided.');
          } else if (data.detail) {
            // Display the detail message if no invalidParams
            setError(data.detail);
          } else {
            setError('Invalid data provided. Please check your inputs.');
          }
        } else if (status === 409) {
          // Handle conflict errors (e.g., email already exists)
          setError('Email already registered for this tournament.');
        } else if (status === 404) {
          // Handle not found errors
          setError('Tournament not found or no longer available.');
        } else if (status >= 500) {
          // Handle server errors
          setError('Server error. Please try again later.');
        } else {
          // Handle other client errors
          if (data.detail) {
            setError(data.detail);
          } else if (data.title) {
            setError(data.title);
          } else {
            setError(`Registration failed with status ${status}. Please try again.`);
          }
        }
      } else if (error.request) {
        // Handle network errors
        setError('Network error. Please check your connection and try again.');
      } else {
        // Handle other errors
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePlayer = async (playerData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await playerAPI.update(playerData.id, playerData);
      console.log('Player updated:', response);
      // Reload players after updating
      await loadPlayers();
      console.log('Player updated successfully!');
    } catch (error) {
      console.error('Error updating player:', error);
      
      // Handle different types of API errors
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle validation errors
          if (data.invalidParams && data.invalidParams.length > 0) {
            // Display the first invalid parameter message
            const firstError = data.invalidParams[0];
            setError(firstError.message || 'Invalid input provided.');
          } else if (data.detail) {
            // Display the detail message if no invalidParams
            setError(data.detail);
          } else {
            setError('Invalid data provided. Please check your inputs.');
          }
        } else if (status === 409) {
          // Handle conflict errors (e.g., email already exists)
          setError('Email already registered for this tournament.');
        } else if (status === 404) {
          // Handle not found errors
          setError('Player or tournament not found.');
        } else if (status >= 500) {
          // Handle server errors
          setError('Server error. Please try again later.');
        } else {
          // Handle other client errors
          if (data.detail) {
            setError(data.detail);
          } else if (data.title) {
            setError(data.title);
          } else {
            setError(`Update failed with status ${status}. Please try again.`);
          }
        }
      } else if (error.request) {
        // Handle network errors
        setError('Network error. Please check your connection and try again.');
      } else {
        // Handle other errors
        setError('Update failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const deletePlayer = async (playerId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await playerAPI.delete(playerId);
      console.log('Player deleted:', response);
      // Reload players after deleting
      await loadPlayers();
      console.log('Player deleted successfully!');
    } catch (error) {
      console.error('Error deleting player:', error);
      setError(`Failed to delete player: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const logout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');
  const [tournamentFilter, setTournamentFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [playerStep, setPlayerStep] = useState('form'); // 'form', 'payment', 'confirmation'
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [playerData, setPlayerData] = useState({
    firstName: '',
    lastName: '',
    teamName: '',
    email: '',
    phone: '',
    gender: '',
    ageUnder15: false,
    skillLevel: '',
    tournamentId: '',
    tournamentName: '',
    registrationDate: '',
    parentGuardianName: '',
    consent: false
  });

  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin' },
    { text: 'Tournaments', icon: <SportsTennis />, path: '/admin/tournaments' },
    { text: 'Players', icon: <People />, path: '/admin/players' }
  ];

  // Load players and tournaments when component mounts
  useEffect(() => {
    loadPlayers();
    loadTournaments();
  }, []);

  const handleCreatePlayer = () => {
    setEditingPlayer(null);
    setPlayerStep('form');
    setPaymentScreenshot(null);
    setPlayerData({
      firstName: '',
      lastName: '',
      teamName: '',
      email: '',
      phone: '',
      gender: '',
      ageUnder18: '',
      skillLevel: '',
      tournamentId: '',
      tournamentName: '',
      registrationDate: '',
      consent: false
    });
    setDialogOpen(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setPlayerData({
      firstName: player.firstName,
      lastName: player.lastName,
      teamName: player.teamName || '',
      email: player.email,
      phone: player.phone,
      gender: player.gender || '',
      ageUnder18: player.ageUnder18 === 'true' || player.ageUnder18 === true, // Proper string/boolean conversion
      skillLevel: player.skillLevel,
      tournamentId: player.tournamentName || '', // Use tournamentName for the form value
      tournamentName: player.tournamentName || '', // Keep tournamentName
      registrationDate: player.registrationDate || '',
      parentGuardianName: player.parentGuardianName || '',
      consent: player.consent || false
    });
    setDialogOpen(true);
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Utility function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle file upload for payment screenshot
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPaymentScreenshot(file);
    }
  };

  // Get selected tournament for payment info
  const getSelectedTournament = () => {
    return tournaments.find(t => t.name === playerData.tournamentName);
  };

  // Render player form (Step 1)
  const renderPlayerForm = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
        {/* Scroll indicator */}
        {true && (
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid #e0e0e0',
            py: 1.5,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ 
                color: '#1976d2', 
                fontWeight: 500,
                fontSize: '0.875rem'
              }}>
                Scroll down to see all fields
              </Typography>
              <Box sx={{
                animation: 'bounce 2s infinite',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)'
                  },
                  '40%': {
                    transform: 'translateY(-4px)'
                  },
                  '60%': {
                    transform: 'translateY(-2px)'
                  }
                }
              }}>
                ↓
              </Box>
            </Box>
            <Typography variant="caption" sx={{ 
              color: '#666', 
              fontSize: '0.75rem',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              Don't forget to read and accept the terms at the bottom
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={3} sx={{ mt: 1, '& .MuiGrid-item': { width: '100%', flexBasis: '100%', maxWidth: '100%' } }}>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <TextField
              fullWidth
              label="First Name"
              value={playerData.firstName}
              onChange={(e) => setPlayerData({...playerData, firstName: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <TextField
              fullWidth
              label="Last Name"
              value={playerData.lastName}
              onChange={(e) => setPlayerData({...playerData, lastName: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <TextField
              fullWidth
              label="Team Name"
              value={playerData.teamName}
              onChange={(e) => setPlayerData({...playerData, teamName: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={playerData.email}
              onChange={(e) => setPlayerData({...playerData, email: e.target.value})}
              required
              variant="outlined"
              error={playerData.email && !isValidEmail(playerData.email)}
              helperText={playerData.email && !isValidEmail(playerData.email) ? 'Please enter a valid email address' : ''}
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={playerData.phone}
              onChange={(e) => setPlayerData({...playerData, phone: e.target.value})}
              required
            />
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <FormControl 
              fullWidth 
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            >
              <InputLabel>Gender</InputLabel>
              <Select
                value={playerData.gender}
                onChange={(e) => setPlayerData({...playerData, gender: e.target.value})}
                label="Gender"
              >
                <MenuItem value="M">M</MenuItem>
                <MenuItem value="F">F</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <FormControl 
              fullWidth 
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            >
              <InputLabel>Under 18?</InputLabel>
              <Select
                value={playerData.ageUnder18 ? 'true' : 'false'}
                onChange={(e) => setPlayerData({...playerData, ageUnder18: e.target.value === 'true'})}
                label="Under 18?"
              >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {playerData.ageUnder18 && (
            <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
              <TextField
                fullWidth
                required
                variant="outlined"
                label="Parent/Guardian Name"
                value={playerData.parentGuardianName}
                onChange={(e) => setPlayerData({...playerData, parentGuardianName: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: '#1976d2',
                    },
                    '&.MuiInputLabel-shrink': {
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                    },
                  },
                }}
              />
            </Grid>
          )}
          
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <FormControl 
              fullWidth 
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  '&.Mui-focused': {
                    color: '#1976d2',
                  },
                  '&.MuiInputLabel-shrink': {
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                  },
                },
              }}
            >
              <InputLabel id="skill-level-label">Skill Level</InputLabel>
              <Select
                labelId="skill-level-label"
                value={playerData.skillLevel}
                onChange={(e) => setPlayerData({...playerData, skillLevel: e.target.value})}
                label="Skill Level"
              >
                {skillLevels.map((level) => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <FormControl 
              fullWidth 
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  '&.Mui-focused': {
                    color: '#1976d2',
                  },
                  '&.MuiInputLabel-shrink': {
                    backgroundColor: 'white',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                  },
                },
              }}
            >
              <InputLabel id="tournament-label">Tournament</InputLabel>
              <Select
                labelId="tournament-label"
                value={playerData.tournamentName}
                onChange={(e) => setPlayerData({...playerData, tournamentName: e.target.value})}
                label="Tournament"
              >
                {tournaments.map((tournament) => (
                  <MenuItem key={tournament.id} value={tournament.name}>
                    {tournament.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', flexBasis: '100%', maxWidth: '100%' }}>
            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              VeiledKnights Tournament Waiver, Release of Liability & Data Consent
            </Typography>
            <Box sx={{ 
              maxHeight: '200px', 
              overflowY: 'auto', 
              border: '1px solid #e0e0e0', 
              borderRadius: 1, 
              p: 2, 
              mb: 2,
              backgroundColor: '#f9f9f9'
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {(() => {
                  const selectedTournament = tournaments.find(t => t.name === playerData.tournamentName);
                  return selectedTournament?.waiver || 'Please select a tournament to view the waiver information.';
                })()}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={playerData.consent}
                  onChange={(e) => setPlayerData({...playerData, consent: e.target.checked})}
                  color="primary"
                />
              }
              label={(() => {
                const selectedTournament = tournaments.find(t => t.name === playerData.tournamentName);
                return selectedTournament?.acknowledgment || "I agree to the terms and conditions";
              })()}
              sx={{ alignItems: 'flex-start' }}
            />
          </Grid>
        </Grid>
        
        {/* Floating completion indicator */}
        {true && (() => {
          const totalFields = 8 + (playerData.ageUnder18 ? 1 : 0);
          const completedFields = [
            playerData.firstName,
            playerData.lastName,
            playerData.teamName,
            playerData.email && isValidEmail(playerData.email),
            playerData.phone,
            playerData.gender,
            playerData.skillLevel,
            playerData.tournamentName,
            playerData.consent,
            ...(playerData.ageUnder18 ? [playerData.parentGuardianName] : [])
          ].filter(Boolean).length;
          const isComplete = completedFields === totalFields;
          
          return (
            <Box sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {/* Floating completion dots */}
              <Box sx={{
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                px: 2,
                py: 1,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
                transform: isComplete ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}>
                {Array.from({ length: totalFields }, (_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: index < completedFields ? '#1976d2' : '#e0e0e0',
                      transition: 'all 0.3s ease',
                      animation: index < completedFields ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                        '100%': { opacity: 1 }
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Completion checkmark */}
              {isComplete && (
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  animation: 'bounceIn 0.6s ease',
                  '@keyframes bounceIn': {
                    '0%': { transform: 'scale(0)' },
                    '50%': { transform: 'scale(1.2)' },
                    '100%': { transform: 'scale(1)' }
                  }
                }}>
                  ✓
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>
    );
  };

  // Render payment step (Step 2)
  const renderPaymentStep = () => {
    const selectedTournament = getSelectedTournament();
    
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            💳 Complete Your Registration
          </Typography>
          <Typography variant="body2">
            Please pay the registration fee via {selectedTournament?.registrationFeePaymentSystem || 'Zelle'} to finish the registration and secure your spot in the tournament.
          </Typography>
        </Alert>
        
        {/* Tournament Details */}
        <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
            Tournament Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Player:</strong> {playerData.firstName} {playerData.lastName}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Tournament:</strong> {selectedTournament?.name}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Registration Fee:</strong> {selectedTournament?.currencyCode === 'USD' ? '$' : ''}{selectedTournament?.registrationFee} {selectedTournament?.currencyCode || 'USD'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              <strong>Payment Method:</strong> {selectedTournament?.registrationFeePaymentSystem || 'Zelle'}
            </Typography>
          </Box>
        </Paper>
        
        {/* Payment Options */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
          {/* QR Code Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
              Scan with your banking app
            </Typography>
            {selectedTournament?.registrationFeePaymentUrl && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'white', 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                display: 'inline-block',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selectedTournament.registrationFeePaymentUrl)}`}
                  alt="Payment QR Code"
                  style={{ width: '200px', height: '200px' }}
                />
              </Box>
            )}
          </Box>
          
          {/* OR Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Typography variant="body2" sx={{ mx: 2, color: '#666', fontWeight: 500 }}>
              OR
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>
          
          {/* Mobile Payment Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
              Pay with {selectedTournament?.registrationFeePaymentSystem || 'Zelle'}
            </Typography>
            {selectedTournament?.registrationFeePaymentUrl && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Payment />}
                onClick={() => window.open(selectedTournament.registrationFeePaymentUrl, '_blank')}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Open Payment Link
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Screenshot Upload */}
        <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a365d' }}>
            Upload Payment Confirmation
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Please upload a screenshot of your payment confirmation to complete your registration.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="payment-screenshot-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="payment-screenshot-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                  }
                }}
              >
                {paymentScreenshot ? 'Change Screenshot' : 'Upload Screenshot'}
              </Button>
            </label>
            {paymentScreenshot && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500 }}>
                  ✓ {paymentScreenshot.name} uploaded successfully
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  };

  // Render confirmation step (Step 3)
  const renderConfirmationStep = () => {
    const selectedTournament = getSelectedTournament();
    
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', textAlign: 'center' }}>
        <Box sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#4caf50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          animation: 'bounceIn 0.6s ease',
          '@keyframes bounceIn': {
            '0%': { transform: 'scale(0)' },
            '50%': { transform: 'scale(1.2)' },
            '100%': { transform: 'scale(1)' }
          }
        }}>
          <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a365d', mb: 2 }}>
          Registration Complete! 🎉
        </Typography>
        
        <Typography variant="h6" sx={{ color: '#4a5568', mb: 3, maxWidth: '500px' }}>
          Congratulations! You have successfully registered for {selectedTournament?.name} and your payment has been confirmed.
        </Typography>
        
        <Paper sx={{ p: 4, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 3, maxWidth: '400px', width: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1a365d' }}>
            Registration Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Name:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {playerData.firstName} {playerData.lastName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Email:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{playerData.email}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Tournament:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedTournament?.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Registration Fee:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedTournament?.currencyCode === 'USD' ? '$' : ''}{selectedTournament?.registrationFee} {selectedTournament?.currencyCode || 'USD'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Status:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#4caf50' }}>
                ✓ Paid & Confirmed
              </Typography>
            </Box>
          </Box>
        </Paper>
        
        <Typography variant="body2" sx={{ color: '#666', maxWidth: '500px' }}>
          You will receive a confirmation email shortly with tournament details and next steps. 
          Thank you for choosing VeiledKnights!
        </Typography>
      </Box>
    );
  };

  // Validation function to check if all required fields are filled and consent is given
  const isFormValid = () => {
    const basicValidation = playerData.firstName && 
           playerData.lastName && 
           playerData.teamName && 
           playerData.email && 
           isValidEmail(playerData.email) && // Added email validation
           playerData.phone && 
           playerData.gender && 
           playerData.skillLevel && 
           playerData.tournamentName && 
           playerData.consent;
    
    // If under 18 is true, parent/guardian name is required
    if (playerData.ageUnder18) {
      return basicValidation && playerData.parentGuardianName;
    }
    
    return basicValidation;
  };

  // Handle payment completion
  const handlePaymentComplete = async () => {
    try {
      setLoading(true);
      
      // Convert screenshot to base64
      let screenshotBase64 = null;
      if (paymentScreenshot) {
        screenshotBase64 = await convertToBase64(paymentScreenshot);
      }
      
      // Create player with payment info
      const now = Date.now();
    const selectedTournament = tournaments.find(t => t.name === playerData.tournamentName);
    const tournamentId = selectedTournament ? selectedTournament.id.toString() : playerData.tournamentId;
    
    const player = {
      ...playerData,
        id: editingPlayer ? editingPlayer.id : uuidv4(),
        tournamentId: tournamentId,
        tournamentName: playerData.tournamentName || '',
      registrationDate: editingPlayer ? editingPlayer.registrationDate : now,
      createdAt: editingPlayer ? editingPlayer.createdAt : now,
        updatedAt: now,
        ageUnder18: Boolean(playerData.ageUnder18),
        registrationFeePaid: true,
        registrationFeeScreenshot: screenshotBase64
      };

    if (editingPlayer) {
      updatePlayer({ ...player, id: editingPlayer.id });
    } else {
      addPlayer(player);
    }

      // Move to confirmation step
      setPlayerStep('confirmation');
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset player registration
  const resetPlayerRegistration = () => {
    setPlayerStep('form');
    setPaymentScreenshot(null);
    setEditingPlayer(null);
    setPlayerData({
      firstName: '',
      lastName: '',
      teamName: '',
      email: '',
      phone: '',
      gender: '',
      ageUnder18: false,
      skillLevel: '',
      tournamentId: '',
      tournamentName: '',
      registrationDate: '',
      parentGuardianName: '',
      consent: false
    });
  };

  const handleSavePlayer = () => {
    if (!isFormValid()) {
      alert('Please fill in all required fields and provide consent.');
      return;
    }

    // Check if tournament has registration fee
    const selectedTournament = getSelectedTournament();
    if (selectedTournament?.registrationFee) {
      // Move to payment step
      setPlayerStep('payment');
    } else {
      // No payment required, create player directly
      const now = Date.now();
      const tournamentId = selectedTournament ? selectedTournament.id.toString() : playerData.tournamentId;
      
      const player = {
        ...playerData,
        id: editingPlayer ? editingPlayer.id : uuidv4(),
        tournamentId: tournamentId,
        tournamentName: playerData.tournamentName || '',
        registrationDate: editingPlayer ? editingPlayer.registrationDate : now,
        createdAt: editingPlayer ? editingPlayer.createdAt : now,
        updatedAt: now,
        ageUnder18: Boolean(playerData.ageUnder18)
      };

      if (editingPlayer) {
        updatePlayer({ ...player, id: editingPlayer.id });
      } else {
        addPlayer(player);
      }

      setDialogOpen(false);
      resetPlayerRegistration();
    }
  };

  const handleDeletePlayer = (player) => {
    setPlayerToDelete(player);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (playerToDelete) {
      deletePlayer(playerToDelete.id);
      setDeleteDialogOpen(false);
      setPlayerToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPlayerToDelete(null);
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = 
      player.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSkill = skillFilter === 'all' || player.skillLevel === skillFilter;
    const matchesTournament = tournamentFilter === 'all' || player.tournamentName === tournamentFilter;
    
    return matchesSearch && matchesSkill && matchesTournament;
  });

  const paginatedPlayers = filteredPlayers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getPlayerStats = () => {
    const totalPlayers = players.length;
    const skillDistribution = skillLevels.reduce((acc, skill) => {
      acc[skill] = players.filter(p => p.skillLevel === skill).length;
      return acc;
    }, {});
    
    // Get unique tournaments from players data
    const uniqueTournaments = [...new Set(players.map(p => p.tournamentName))].filter(Boolean);
    const tournamentDistribution = uniqueTournaments.map(tournamentName => ({
      name: tournamentName,
      count: players.filter(p => p.tournamentName === tournamentName).length
    }));

    return { totalPlayers, skillDistribution, tournamentDistribution };
  };

  const stats = getPlayerStats();


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="primary"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={require('../images/new_logo.png')}
              alt="VeiledKnights Logo"
              style={{
                width: '40px',
                height: '40px',
                marginRight: '12px',
                borderRadius: '0px'
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                color: '#1a365d',
                fontWeight: 700,
                letterSpacing: '-0.01em'
              }}
            >
            VeiledKnights
          </Typography>
          </Box>
          <Button 
            color="primary" 
            onClick={logout} 
            startIcon={<Logout />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', height: '100%' }}>
          {/* Admin Portal Header */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            textAlign: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            mb: 1
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: '#1a365d',
                background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Admin Portal
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ 
            mx: 2, 
            mb: 1,
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #1976d2 50%, transparent 100%)',
            borderRadius: '1px'
          }} />

          {/* Navigation Menu */}
          <List sx={{ px: 2 }}>
            {menuItems.map((item, index) => (
              <ListItem 
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: item.path === '/admin/players' 
                    ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.08) 100%)'
                    : 'transparent',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: '#1976d2',
                  '& .MuiSvgIcon-root': {
                    fontSize: 24
                  }
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      color: '#1a365d',
                      fontSize: '0.95rem'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ mx: 2, my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

          {/* Public Site Link */}
          <List sx={{ px: 2 }}>
            <ListItem 
              onClick={() => navigate('/')}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  transform: 'translateX(4px)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.2)'
                }
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: '#1976d2',
                '& .MuiSvgIcon-root': {
                  fontSize: 24
                }
              }}>
                <Home />
              </ListItemIcon>
              <ListItemText 
                primary="Public Site"
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    color: '#1a365d',
                    fontSize: '0.95rem'
                  }
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          mt: 8,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Container maxWidth="xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ 
              textAlign: 'center', 
              mb: 6,
              background: 'rgba(255, 255, 255, 0.7)',
              borderRadius: 3,
              p: 4,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em'
                }}
              >
                  Player Management
                </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#4a5568',
                  fontWeight: 400,
                  maxWidth: '600px',
                  mx: 'auto',
                  mb: 3
                }}
              >
                  Manage player registrations and information
                </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreatePlayer}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Add Player
              </Button>
            </Box>
          </motion.div>

          {/* Stats Cards */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ width: '100%' }}
              >
                <Card sx={{ 
                  width: '100%', 
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    borderColor: '#1976d2'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                      }}>
                        <People sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" component="div" sx={{ 
                        fontWeight: 600,
                        color: '#1a365d'
                      }}>
                        Total Players
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ 
                      fontWeight: 800,
                      mb: 1,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {stats.totalPlayers}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#4a5568',
                      fontWeight: 500
                    }}>
                      registered across all tournaments
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{ width: '100%' }}
              >
                <Card sx={{ 
                  width: '100%', 
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    borderColor: '#1976d2'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                      }}>
                        <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" component="div" sx={{ 
                        fontWeight: 600,
                        color: '#1a365d'
                      }}>
                        Expert Players
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ 
                      fontWeight: 800,
                      mb: 1,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {stats.skillDistribution.Expert || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#4a5568',
                      fontWeight: 500
                    }}>
                      highest skill level
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                style={{ width: '100%' }}
              >
                <Card sx={{ 
                  width: '100%', 
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    borderColor: '#1976d2'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                      }}>
                        <SportsTennis sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" component="div" sx={{ 
                        fontWeight: 600,
                        color: '#1a365d'
                      }}>
                        Active Tournaments
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ 
                      fontWeight: 800,
                      mb: 1,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {stats.tournamentDistribution.length}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#4a5568',
                      fontWeight: 500
                    }}>
                      with player registrations
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                style={{ width: '100%' }}
              >
                <Card sx={{ 
                  width: '100%', 
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    borderColor: '#1976d2'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                      }}>
                        <Event sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" component="div" sx={{ 
                        fontWeight: 600,
                        color: '#1a365d'
                      }}>
                        Recent Registrations
                      </Typography>
                    </Box>
                    <Typography variant="h3" component="div" sx={{ 
                      fontWeight: 800,
                      mb: 1,
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {players.filter(p => {
                        const registrationDate = new Date(p.registrationDate);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return registrationDate > weekAgo;
                      }).length}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#4a5568',
                      fontWeight: 500
                    }}>
                      in the last 7 days
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Card sx={{ 
              mb: 4,
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#1a365d',
                    mb: 3,
                    textAlign: 'center'
                  }}
                >
                  Search & Filter Players
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 2 }} alignItems="center">
                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Search players..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Skill Level</InputLabel>
                      <Select
                        value={skillFilter}
                        onChange={(e) => setSkillFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Levels</MenuItem>
                        {skillLevels.map((level) => (
                          <MenuItem key={level} value={level}>{level}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Tournament</InputLabel>
                      <Select
                        value={tournamentFilter}
                        onChange={(e) => setTournamentFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Tournaments</MenuItem>
                        {stats.tournamentDistribution.map((tournament, index) => (
                          <MenuItem key={index} value={tournament.name}>
                            {tournament.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                      Showing {filteredPlayers.length} of {players.length} players
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>

          {/* Players Table/Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6">Loading players...</Typography>
              </Box>
            ) : error ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="error">
                  {error}
                </Typography>
              </Box>
            ) : isMobile ? (
              // Mobile Card Layout
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {paginatedPlayers.map((player) => (
                  <Card key={player.id} sx={{ 
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      borderColor: '#1976d2'
                    }
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Box sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)'
                        }}>
                          <Person sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700,
                          color: '#1a365d'
                        }}>
                          {player.firstName} {player.lastName}
                        </Typography>
                      </Box>
                      <Chip 
                        label={player.skillLevel} 
                        sx={{
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          color: 'white',
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                        }}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Email sx={{ mr: 1, color: '#1976d2', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>{player.email}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Phone sx={{ mr: 1, color: '#1976d2', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>{player.phone}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                        <strong>Tournament:</strong> {player.tournamentName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                        <strong>Registered:</strong> {new Date(player.registrationDate).toLocaleDateString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, mr: 1 }}>
                          <strong>Payment:</strong>
                        </Typography>
                        <Chip 
                          label={player.registrationFeePaid ? 'Paid' : 'Pending'} 
                          sx={{
                            fontWeight: 600,
                            background: player.registrationFeePaid 
                              ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                              : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                            color: 'white',
                            border: 'none',
                            boxShadow: player.registrationFeePaid 
                              ? '0 2px 8px rgba(76, 175, 80, 0.3)'
                              : '0 2px 8px rgba(255, 152, 0, 0.3)'
                          }}
                          size="small"
                        />
                      </Box>
                      {player.registrationFeeScreenshot && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500, mr: 1 }}>
                            <strong>Screenshot:</strong>
                          </Typography>
                          <img 
                            src={player.registrationFeeScreenshot} 
                            alt="Payment Screenshot" 
                            style={{ 
                              width: '30px', 
                              height: '30px', 
                              borderRadius: '4px',
                              objectFit: 'cover',
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0'
                            }}
                            onClick={() => {
                              // Open image in new tab for full view
                              const newWindow = window.open();
                              newWindow.document.write(`
                                <html>
                                  <head><title>Payment Screenshot</title></head>
                                  <body style="margin:0; padding:20px; background:#f5f5f5; display:flex; justify-content:center; align-items:center; min-height:100vh;">
                                    <img src="${player.registrationFeeScreenshot}" style="max-width:100%; max-height:100%; border-radius:8px; box-shadow:0 4px 20px rgba(0,0,0,0.1);" />
                                  </body>
                                </html>
                              `);
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', ml: 1 }}>
                            Click to view
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button 
                        size="small" 
                        startIcon={<Edit />}
                        onClick={() => handleEditPlayer(player)}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          borderWidth: 2,
                          '&:hover': {
                            borderColor: '#1565c0',
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="small" 
                        startIcon={<Delete />}
                        onClick={() => handleDeletePlayer(player)}
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 2,
                          textTransform: 'none',
                          borderColor: '#f44336',
                          color: '#f44336',
                          borderWidth: 2,
                          '&:hover': {
                            borderColor: '#d32f2f',
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            transform: 'translateY(-1px)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Card>
                ))}
                
                {/* Mobile Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredPlayers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      '& .MuiTablePagination-toolbar': {
                        paddingLeft: 0,
                        paddingRight: 0,
                      }
                    }}
                  />
                </Box>
              </Box>
            ) : (
              // Desktop Table Layout
              <Card sx={{
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ 
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        '& .MuiTableCell-head': {
                          fontWeight: 700,
                          color: '#1a365d',
                          fontSize: '0.9rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }
                      }}>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Skill Level</TableCell>
                        <TableCell>Tournament</TableCell>
                        <TableCell>Registration Date</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Payment Screenshot</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedPlayers.map((player) => (
                        <TableRow key={player.id} hover sx={{ 
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                              }}>
                                <Person sx={{ color: 'white', fontSize: 16 }} />
                              </Box>
                              <Typography sx={{ fontWeight: 600, color: '#1a365d' }}>
                              {player.firstName} {player.lastName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Email sx={{ mr: 1, color: '#1976d2', fontSize: 16 }} />
                              <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>
                              {player.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Phone sx={{ mr: 1, color: '#1976d2', fontSize: 16 }} />
                              <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>
                              {player.phone}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={player.skillLevel} 
                              sx={{
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                color: 'white',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>
                              {player.tournamentName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>
                            {new Date(player.registrationDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={player.registrationFeePaid ? 'Paid' : 'Pending'} 
                              sx={{
                                fontWeight: 600,
                                background: player.registrationFeePaid 
                                  ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                                  : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                                color: 'white',
                                border: 'none',
                                boxShadow: player.registrationFeePaid 
                                  ? '0 2px 8px rgba(76, 175, 80, 0.3)'
                                  : '0 2px 8px rgba(255, 152, 0, 0.3)'
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {player.registrationFeeScreenshot ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img 
                                  src={player.registrationFeeScreenshot} 
                                  alt="Payment Screenshot" 
                                  style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                    border: '1px solid #e0e0e0'
                                  }}
                                  onClick={() => {
                                    // Open image in new tab for full view
                                    const newWindow = window.open();
                                    newWindow.document.write(`
                                      <html>
                                        <head><title>Payment Screenshot</title></head>
                                        <body style="margin:0; padding:20px; background:#f5f5f5; display:flex; justify-content:center; align-items:center; min-height:100vh;">
                                          <img src="${player.registrationFeeScreenshot}" style="max-width:100%; max-height:100%; border-radius:8px; box-shadow:0 4px 20px rgba(0,0,0,0.1);" />
                                        </body>
                                      </html>
                                    `);
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                  Click to view
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                                No screenshot
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button 
                                size="small" 
                                startIcon={<Edit />}
                                onClick={() => handleEditPlayer(player)}
                                sx={{
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  borderColor: '#1976d2',
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                                variant="outlined"
                              >
                                Edit
                              </Button>
                              <Button 
                                size="small" 
                                startIcon={<Delete />}
                                onClick={() => handleDeletePlayer(player)}
                                sx={{
                                  fontWeight: 600,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  borderColor: '#f44336',
                                  color: '#f44336',
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                                variant="outlined"
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredPlayers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Card>
            )}
          </motion.div>

          {/* Create/Edit Player Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={() => {
              setDialogOpen(false);
              setEditingPlayer(null);
              // Reset form data
              setPlayerData({
                firstName: '',
                lastName: '',
                teamName: '',
                email: '',
                phone: '',
                gender: '',
                ageUnder18: false,
                skillLevel: '',
                tournamentId: '',
                tournamentName: '',
                registrationDate: '',
                parentGuardianName: '',
                consent: false
              });
            }}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
              {/* Full Width Divider with Logo */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 3,
                position: 'relative',
                width: '100%'
              }}>
                {/* Left line - extends to edge */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  background: 'linear-gradient(to right, transparent, #e2e8f0)',
                  mr: 2
                }} />
                
                {/* Logo */}
                <img
                  src={require('../images/new_logo.png')}
                  alt="VeiledKnights Logo"
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    borderRadius: '0px'
                  }}
                />
                
                {/* Right line - extends to edge */}
                <Box sx={{
                  height: '2px',
                  flex: 1,
                  background: 'linear-gradient(to right, #e2e8f0, transparent)',
                  ml: 2
                }} />
              </Box>
              
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                color: '#1a365d',
                background: 'linear-gradient(135deg, #1a365d 0%, #1976d2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
              {editingPlayer ? 'Edit Player' : 'Add New Player'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
                {/* Stepper Header */}
                <Stepper activeStep={playerStep === 'form' ? 0 : playerStep === 'payment' ? 1 : 2} sx={{ mb: 4 }}>
                  <Step>
                    <StepLabel>Player Information</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Payment</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Confirmation</StepLabel>
                  </Step>
                </Stepper>

                {/* Step Content */}
                {playerStep === 'form' && renderPlayerForm()}
                {playerStep === 'payment' && renderPaymentStep()}
                {playerStep === 'confirmation' && renderConfirmationStep()}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              {playerStep === 'form' && (
                <>
                  <Button 
                    onClick={() => {
                      setDialogOpen(false);
                      resetPlayerRegistration();
                    }}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSavePlayer}
                    disabled={!isFormValid()}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      background: isFormValid() 
                        ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                        : 'rgba(0, 0, 0, 0.12)',
                      color: isFormValid() ? 'white' : 'rgba(0, 0, 0, 0.26)',
                      boxShadow: isFormValid() 
                        ? '0 4px 15px rgba(25, 118, 210, 0.3)'
                        : 'none',
                      '&:hover': isFormValid() ? {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                        transform: 'translateY(-1px)'
                      } : {},
                      '&:disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                        boxShadow: 'none'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {editingPlayer ? 'Update' : 'Continue to Payment'}
                  </Button>
                </>
              )}
              
              {playerStep === 'payment' && (
                <>
                  <Button 
                    onClick={() => setPlayerStep('form')}
                    startIcon={<ArrowBack />}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    variant="outlined"
                  >
                    Back
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handlePaymentComplete}
                    disabled={!paymentScreenshot}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      background: paymentScreenshot 
                        ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                        : 'rgba(0, 0, 0, 0.12)',
                      color: paymentScreenshot ? 'white' : 'rgba(0, 0, 0, 0.26)',
                      boxShadow: paymentScreenshot 
                        ? '0 4px 15px rgba(25, 118, 210, 0.3)'
                        : 'none',
                      '&:hover': paymentScreenshot ? {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                        transform: 'translateY(-1px)'
                      } : {},
                      '&:disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                        boxShadow: 'none'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Complete Registration
                  </Button>
                </>
              )}
              
              {playerStep === 'confirmation' && (
                <Button 
                  variant="contained" 
                  onClick={() => {
                    setDialogOpen(false);
                    resetPlayerRegistration();
                  }}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    color: 'white',
                    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
                      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Done
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog 
            open={deleteDialogOpen} 
            onClose={cancelDelete}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Delete color="error" />
                <Typography variant="h6" component="span">
                  Delete Player
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete this player:
              </Typography>
              {playerToDelete && (
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  mb: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {playerToDelete.firstName} {playerToDelete.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {playerToDelete.email}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Phone:</strong> {playerToDelete.phone}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Skill Level:</strong> {playerToDelete.skillLevel}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Tournament:</strong> {playerToDelete.tournamentName}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                All player data and registrations will be permanently removed.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={cancelDelete}
                variant="outlined"
                sx={{ 
                  mr: 1,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                variant="contained"
                startIcon={<Delete />}
                sx={{
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                  boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                    boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Delete Player
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default PlayerManagement;
